import { useEffect, useMemo, useRef } from "react";
import { Activity } from "lucide-react";
import {
  usePublicEmotionalSignal,
  type PublicEmotionalCtx,
} from "@/hooks/usePublicEmotionalSignal";
import type { StoryPhase } from "@/lib/viralIntel/storyArcEngine";
import { trackEvent } from "@/lib/audit";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { HelpCircle } from "lucide-react";

/**
 * Cinematic Story Arc Ribbon — one dominant phase, broadcast feel.
 * Reuses StoryArcEngine via usePublicEmotionalSignal. No new polling.
 *
 * Hidden when no active match (idle + minute 0).
 */

type CinematicPhase =
  | "tactical_opening"
  | "crowd_ignition"
  | "pressure_build"
  | "chaos_window"
  | "comeback_phase"
  | "tactical_lockdown"
  | "last_minute_survival"
  | "emotional_explosion";

const COPY: Record<CinematicPhase, { es: string; color: string; emoji: string; desc: string }> = {
  tactical_opening:     { es: "Apertura Táctica",        color: "oklch(0.7 0.14 220)", emoji: "♟️", desc: "Primeros minutos: los equipos estudian al rival, posesión cauta y poco riesgo." },
  crowd_ignition:       { es: "Ignición de la Hinchada", color: "oklch(0.78 0.2 60)",  emoji: "🔥", desc: "La afición empuja: cánticos, ritmo alto y llegadas claras al área." },
  pressure_build:       { es: "Presión Creciente",       color: "oklch(0.72 0.22 30)", emoji: "⚡", desc: "Intensidad táctica subiendo: más duelos, presión alta y posesión en campo rival. El partido se calienta." },
  chaos_window:         { es: "Ventana de Caos",         color: "oklch(0.62 0.28 20)", emoji: "💥", desc: "Transiciones rápidas en ambas áreas: ocasiones constantes y descontrol táctico." },
  comeback_phase:       { es: "Fase de Remontada",       color: "oklch(0.78 0.18 145)", emoji: "🚀", desc: "El equipo que pierde acelera: vuelca el campo y empuja por igualar o dar la vuelta." },
  tactical_lockdown:    { es: "Bloqueo Táctico",         color: "oklch(0.55 0.1 250)",  emoji: "🛡️", desc: "Defensa replegada y bloques bajos: se prioriza no encajar sobre crear." },
  last_minute_survival: { es: "Supervivencia Final",     color: "oklch(0.65 0.25 0)",   emoji: "⏱️", desc: "Minutos finales con resultado ajustado: cada balón puede definir el partido." },
  emotional_explosion:  { es: "Explosión Emocional",     color: "oklch(0.7 0.27 330)",  emoji: "🎆", desc: "Clímax del partido: gol decisivo, agonía o festejo masivo de la hinchada." },
};

function mapPhase(phase: StoryPhase, minute: number): CinematicPhase {
  if (phase === "desperation") return minute >= 88 ? "emotional_explosion" : "last_minute_survival";
  if (phase === "comeback") return "comeback_phase";
  if (phase === "collapse") return "chaos_window";
  if (phase === "resistance") return "tactical_lockdown";
  if (phase === "dominance") return "crowd_ignition";
  if (phase === "reaction") return "crowd_ignition";
  if (phase === "tension") return "pressure_build";
  return minute <= 12 ? "tactical_opening" : "pressure_build";
}

export function StoryArcSurface({
  ctx,
  minute,
  className = "",
}: {
  ctx: PublicEmotionalCtx;
  minute: number;
  className?: string;
}) {
  const sig = usePublicEmotionalSignal(ctx);
  const phase = useMemo(() => mapPhase(sig.arc.phase, minute), [sig.arc.phase, minute]);
  const meta = COPY[phase];
  const intensityPct = Math.max(8, Math.round(sig.arc.intensity * 100));
  const lastPhase = useRef<CinematicPhase | null>(null);

  useEffect(() => {
    if (lastPhase.current === phase) return;
    lastPhase.current = phase;
    void trackEvent("story_arc_phase_changed", { phase, intensity: sig.arc.intensity, minute });
  }, [phase, sig.arc.intensity, minute]);

  useEffect(() => {
    void trackEvent("story_arc_surface_shown", { phase, minute });
    // shown once per phase change is enough
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  if (!minute || minute <= 0) return null;

  return (
    <TooltipProvider delayDuration={150}>
    <Tooltip>
    <TooltipTrigger asChild>
    <div
      className={`relative flex items-center gap-2 overflow-hidden rounded-lg border border-border bg-[oklch(0.06_0.005_260)] px-3 py-2 text-[13px] shadow-lg ${className}`}
      style={{ boxShadow: `inset 0 0 0 1px ${meta.color}` }}
      aria-label={`Story arc: ${meta.es}`}
      data-story-phase={phase}
      role="button"
      tabIndex={0}
    >
      <span aria-hidden className="text-sm leading-none">{meta.emoji}</span>
      <span className="flex items-center gap-1 font-black uppercase tracking-normal text-[oklch(1_0_0)]">
        <Activity className="h-3 w-3" />
        {meta.es}
      </span>
      <HelpCircle className="h-3 w-3 text-[oklch(1_0_0)]" aria-hidden />
      <span className="ml-auto flex items-center gap-1.5">
        <span
          aria-hidden
          className="h-1.5 w-16 overflow-hidden rounded-full bg-secondary/50"
        >
          <span
            className="block h-full transition-all"
            style={{
              width: `${intensityPct}%`,
              background: `linear-gradient(90deg, ${meta.color}, color-mix(in oklch, ${meta.color} 50%, white))`,
            }}
          />
        </span>
        <span className="font-mono tabular-nums text-[13px] font-black text-[oklch(1_0_0)]">
          {intensityPct}%
        </span>
      </span>
    </div>
    </TooltipTrigger>
    <TooltipContent
      className="max-w-xs space-y-1 rounded-lg border border-border bg-[oklch(0.08_0.005_260)] px-3 py-2 text-[12px] leading-relaxed text-[oklch(0.98_0_0)] shadow-xl"
      sideOffset={6}
    >
      <div className="font-bold tracking-wide text-[oklch(0.98_0_0)]">
        {meta.emoji} {meta.es}
      </div>
      <div className="text-[oklch(0.88_0.01_260)]">{meta.desc}</div>
      <div className="pt-1 text-[10px] text-[oklch(0.78_0.01_260)]">
        Intensidad táctica: {intensityPct}% · minuto {minute}
      </div>
    </TooltipContent>
    </Tooltip>
    </TooltipProvider>
  );
}
